// root '../../../';

// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';
import { extent, max, min } from 'd3-array';
import Zion from './utils/Zion';
import { linearAxis, timeAxis } from '../../../factory/axes';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils & graphic
import { stylePremium } from './utils/helpers';
import config from './config';
import graphic from './graphic';

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});

let chartData;
let rawData;

const margins = {
  top: 20,
  right: 80,
  bottom: 25,
  left: 35,
};

const parser = d3.timeParse('%Y-%m-%d'); // https://github.com/d3/d3-time-format;

// let recessionData;
let minValue;
let maxValue;

let minDomain;
let maxDomain;

let lastData;

let dataNote;
let summary;

let mean2000;
let mean10years;

// Splits data into contiguous monthly segments, breaking wherever a month
// was skipped (e.g. no report released), so those spans can be drawn
// differently rather than implying a real reading exists there.
function splitByGap(data, thresholdDays = 45) {
  let current = [data[0]];
  const segments = [current];
  const gaps = [];

  for (let i = 1; i < data.length; i += 1) {
    const diffDays = (data[i].date - data[i - 1].date) / 86400000;

    if (diffDays > thresholdDays) {
      gaps.push([data[i - 1], data[i]]);
      current = [data[i]];
      segments.push(current);
    } else {
      current.push(data[i]);
    }
  }

  return { segments, gaps };
}

// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

const CNNPolitics = false;
const CNNBusiness = false;
const pTime = d3.timeParse('%Y-%m-%d');

stylePremium(CNNPolitics, CNNBusiness);

const rParser = d3.timeParse('%m/%d/%Y');
const recessionData = [
  { start: '7/1/1990', end: '3/1/1991' },
  { start: '3/1/2001', end: '11/1/2001' },
  { start: '12/1/2007', end: '6/1/2009' },
  { start: '2/1/2020', end: '4/1/2020' },
];

recessionData.forEach((d) => {
  d.start = rParser(d.start);
  d.end = rParser(d.end);
});

// COVID-19 emergency period
const covidEmergency = {
  start: rParser('3/13/2020'),
  end: rParser('5/11/2023'),
};

d3.json('https://ix.cnn.io/data/consumer-price-index/cpi.json').then((jsonData) => {
  const { data } = jsonData;
  rawData = data.map(d => ({
    date: d3.timeParse('%Y-%m-%d')(d.date),
    yr: +d.date.split('-')[0],
    value: d.cpiaucsl,
  }));


  const curYr = rawData[rawData.length - 1].yr || 2024;

  // TODO the amount could be a url param
  const yrsBack = 10;

  mean2000 = jsonData.meanSince2000;
  mean10years = jsonData.mean10years;
  console.log(mean10years);

  chartData = rawData.filter(d => d.date > pTime(`${curYr - 10}-01-01`));

  minDomain = d3.min(chartData, d => d.date);
  maxDomain = d3.max(chartData, d => d.date);

  minValue = 0;
  maxValue = d3.max(chartData, d => d.value + 1);

  lastData = chartData.filter(d => d.date == maxDomain)[0];

  if (jsonData.lastUpdatedStr && jsonData.latestMonthStr) {
    dataNote = `Notes: Data current through ${jsonData.latestMonthStr} as of ${jsonData.lastUpdatedStr}. Figures not adjusted for seasonal swings. Because of the federal government shutdown, the US Bureau of Labor Statistics didn't release an annual inflation value for October 2025. Covid-19 timeline reflects the period of the White House emergency declaration.`;
  }

  if (jsonData.summaryHtml) {
    summary = jsonData.summaryHtml;
  }

  draw();
  window.onresize = draw;
});


function draw() {
  const container = d3.select('#graphic');
  container.html('');

  if (summary) {
    d3.select('.dek').html(summary);
  }

  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;
  const height = 400;

  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;

  const xTicks = isMobile ? 3 : 5;
  const yTicks = isMobile ? 5 : 5;

  const xScale = d3.scaleTime()
    .domain([minDomain, maxDomain])
    .range([0, chartWidth]);

  const yScale = d3.scaleLinear()
    .domain([minValue, maxValue])
    .range([chartHeight, 0]);

  const xAxis = d3.axisBottom()
    .scale(xScale)
    .ticks(xTicks)
    .tickFormat((d, i) => {
      if (i === 0 || d3.timeFormat('%m')(d) == '01') {
        return d3.timeFormat('%b ’%y')(d);
      }
      return d3.timeFormat('%b')(d);
    });

  const yAxis = d3.axisLeft()
    .scale(yScale)
    .ticks(yTicks)
    .tickSize(-chartWidth)
    .tickFormat((d, i, x) => (i == x.length - 1 ? `${d3.format('.0f')(d)}%` : `${d3.format('.0f')(d)}`));

  const path = d3.line()
    .curve(d3.curveMonotoneX)
    .x(d => xScale(d.date))
    .y(d => yScale(d.value));

  const area = d3.area()
    .curve(d3.curveMonotoneX)
    .x(d => xScale(d.date))
    .y0(yScale(0))
    .y1(d => yScale(d.value));

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(${[0, chartHeight]})`)
    .call(xAxis);

  g.append('g')
    .attr('class', 'grid y')
    .call(yAxis);

  const { segments } = splitByGap(chartData);

  // Area fill only under contiguous, actually-reported spans -- skip it
  // across any skipped-month gap so we're not implying data we don't have.
  segments.forEach((seg) => {
    if (seg.length > 1) {
      g.append('path')
        .datum(seg)
        .attr('class', 'area')
        .attr('fill', '#7c4ea5')
        .attr('opacity', 0.12)
        .attr('d', area);
    }
  });

  const line = g.append('g')
    .attr('class', 'line');

  // Each segment is drawn as its own path, so skipped-month gaps are a true
  // break in the line rather than a bridge implying data we don't have.
  segments.forEach((seg) => {
    line.append('path')
      .datum(seg)
      .attr('stroke', '#7c4ea5')
      .attr('stroke-width', '2')
      .attr('fill', 'none')
      .attr('d', path);
  });

  d3.select('.grid.y .domain').remove();

  // Add COVID-19 emergency highlighted area
  if (covidEmergency.start >= minDomain && covidEmergency.start <= maxDomain) {
    const covidStartX = Math.max(0, xScale(covidEmergency.start));
    const covidEndX = Math.min(chartWidth, xScale(covidEmergency.end));

    // Add the shaded rectangle
    g.append('rect')
      .attr('x', covidStartX)
      .attr('y', 0)
      .attr('width', covidEndX - covidStartX)
      .attr('height', chartHeight)
      .attr('fill', '#d3d3d3')
      .attr('fill-opacity', 0.3);

    // Add annotation for COVID emergency
    const covidAnnotationX = covidStartX + (covidEndX - covidStartX) / 2;
    const covidAnnotationY = chartHeight * 0.95; // Position near top

    const covidAnnotation = g.append('g')
      .attr('class', 'covid-annotation')
      .attr('transform', `translate(${covidAnnotationX}, ${covidAnnotationY})`);

    if (isMobile) {
      // Mobile: Split into two lines
      covidAnnotation.append('text')
        .attr('text-anchor', 'middle')
        .attr('fill', '#666')
        .attr('font-size', '12px')
        .attr('font-style', 'italic')
        .text('Covid-19');

      covidAnnotation.append('text')
        .attr('text-anchor', 'middle')
        .attr('dy', '1.2em')
        .attr('fill', '#666')
        .attr('font-size', '12px')
        .attr('font-style', 'italic')
        .text('emergency');
    } else {
      // Desktop: Single line
      covidAnnotation.append('text')
        .attr('text-anchor', 'middle')
        .attr('fill', '#666')
        .attr('font-size', '12px')
        .attr('font-style', 'italic')
        .text('Covid-19 emergency');
    }
  }

  if (dataNote) {
    d3.select('.datanote').html(dataNote);
  }

  // Add the 2% target line
  g.append('line')
    .attr('x1', 0)
    .attr('x2', chartWidth)
    .attr('y1', yScale(mean10years))
    .attr('y2', yScale(mean10years))
    .attr('stroke', '#666')
    .attr('stroke-width', 1)
    .attr('stroke-dasharray', '4,4');

  // Add the annotation for the 2% target
  g.append('text')
    .attr('x', isMobile ? 5 : 10)
    .attr('y', yScale(mean10years) - 8)
    .attr('text-anchor', 'start')
    .attr('fill', '#666')
    .attr('font-size', isMobile ? '12px' : '12px')
    .text(isMobile ? `Avg.: ${mean10years}%` : `10-year average: ${mean10years}%`);

  // Add annotation for the last data point
  const annotation = g.append('g')
    .attr('class', 'annotation')
    .attr('transform', `translate(${[xScale(lastData.date), yScale(lastData.value)]})`);

  annotation.append('circle')
    .attr('class', 'pulse-ring')
    .attr('r', '4')
    .attr('fill', '#7c4ea5');

  annotation.append('circle')
    .attr('class', 'end-dot')
    .attr('r', '4')
    .attr('fill', '#7c4ea5')
    .attr('stroke', 'none');

  annotation.append('text')
    .attr('dx', '10px')
    .attr('class', 'annotation-date')
    .text(d3.timeFormat('%b %Y')(lastData.date));

  annotation.append('text')
    .attr('dx', '10px')
    .attr('class', 'annotation-value')
    .text(`${d3.format('.1f')(lastData.value)}%`)
    .attr('dy', '1.2em');

  window.pymChild.sendHeight();
}
